import { Heart, MessageCircle, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import swal from "../../utils/swal";
import { Drawer } from "../../components/ui";
import {
  useDeleteComment,
  useGetPostComments,
  useGetPostLikes,
  useRemovePostLike,
} from "../../query/postQuery";
import { UPLOADS_URL } from "../../config/constants";
import {
  commentAuthorName,
  commentAuthorRole,
  engagementSummary,
  feedContent,
} from "./postHelpers";

function PersonAvatar({ image, name }) {
  const initials = String(name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  if (image) {
    return <img className="post-engage-avatar" src={`${UPLOADS_URL}/${image}`} alt="" />;
  }
  return <span className="post-engage-avatar is-fallback">{initials || "?"}</span>;
}

export default function PostEngagementDrawer({ post, open, onClose }) {
  const postId = post?._id;
  const { likes: likesHint, comments: commentsHint } = engagementSummary(post || {});

  const { data: commentsData, isLoading: commentsLoading } = useGetPostComments(
    open ? postId : null
  );
  const { data: likesData, isLoading: likesLoading } = useGetPostLikes(open ? postId : null);
  const { mutate: deleteComment, isPending: deletingComment } = useDeleteComment();
  const { mutate: removeLike, isPending: removingLike } = useRemovePostLike();

  const comments = commentsData?.docs || [];
  const likes = likesData?.docs || [];
  const likesCount = likesData?.totalDocs ?? likesHint;
  const commentsCount = commentsData?.totalDocs ?? commentsHint;

  const handleDeleteComment = (comment) => {
    swal({
      title: "Delete comment?",
      text: "This comment will be removed from the post.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      deleteComment(
        { commentId: comment._id, postId },
        {
          onSuccess: () => swal("Deleted", "Comment removed", "success"),
          onError: (error) => swal("Error", error?.message || "Could not delete", "error"),
        }
      );
    });
  };

  const handleRemoveLike = (person) => {
    swal({
      title: "Remove like?",
      text: `Remove ${commentAuthorName(person)}'s like from this post?`,
      icon: "warning",
      buttons: ["Cancel", "Remove"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      removeLike(
        { postId, userId: person._id },
        {
          onSuccess: () => swal("Removed", "Like removed", "success"),
          onError: (error) => swal("Error", error?.message || "Could not remove like", "error"),
        }
      );
    });
  };

  return (
    <Drawer
      title="Likes & comments"
      open={open}
      onClose={onClose}
      width={420}
    >
      {post ? (
        <div className="post-engage-drawer">
          <p className="post-engage-snippet">{feedContent(post.content) || "Post"}</p>

          <div className="post-engage-summary">
            <span>
              <Heart size={14} />
              {likesCount} {likesCount === 1 ? "like" : "likes"}
            </span>
            <span>
              <MessageCircle size={14} />
              {commentsCount} {commentsCount === 1 ? "comment" : "comments"}
            </span>
          </div>

          <section className="post-engage-section">
            <h4>Likes</h4>
            {likesLoading ? (
              <p className="post-engage-empty">Loading likes…</p>
            ) : likes.length === 0 ? (
              <p className="post-engage-empty">No likes yet.</p>
            ) : (
              <ul className="post-engage-list">
                {likes.map((person) => {
                  const name = commentAuthorName(person);
                  const role = commentAuthorRole(person) || person.role || "";
                  return (
                    <li key={String(person._id)} className="post-engage-row">
                      <PersonAvatar image={person.image} name={name} />
                      <div className="post-engage-copy">
                        <strong>{name}</strong>
                        <span>{role ? role.charAt(0).toUpperCase() + role.slice(1) : "User"}</span>
                      </div>
                      <button
                        type="button"
                        className="post-engage-delete"
                        disabled={removingLike}
                        onClick={() => handleRemoveLike(person)}
                        aria-label={`Remove like from ${name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="post-engage-section">
            <h4>Comments</h4>
            {commentsLoading ? (
              <p className="post-engage-empty">Loading comments…</p>
            ) : comments.length === 0 ? (
              <p className="post-engage-empty">No comments yet.</p>
            ) : (
              <ul className="post-engage-list">
                {comments.map((comment) => {
                  const name = commentAuthorName(comment.author);
                  const role = commentAuthorRole(comment.author);
                  return (
                    <li key={comment._id} className="post-engage-row is-comment">
                      <PersonAvatar image={comment.author?.image} name={name} />
                      <div className="post-engage-copy">
                        <div className="post-engage-comment-head">
                          <strong>{name}</strong>
                          {role ? <span className="post-engage-role">{role}</span> : null}
                          <span className="post-engage-time">
                            {comment.createdAt
                              ? dayjs(comment.createdAt).format("D MMM YYYY, h:mm A")
                              : ""}
                          </span>
                        </div>
                        <p className="post-engage-comment-body">{comment.content}</p>
                      </div>
                      <button
                        type="button"
                        className="post-engage-delete"
                        disabled={deletingComment}
                        onClick={() => handleDeleteComment(comment)}
                        aria-label="Delete comment"
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      ) : null}
    </Drawer>
  );
}
